import React, { useContext, useEffect, useState } from "react";
import "./HomePage.scss";
import {
  homeBackground,
  IconBridge,
  IconExperience,
  IconIndustry,
  IconRelationship,
  IconRight,
  IconOptimisation,
  IconEntry,
  IconInsights,
  IconRiskAssessmentSupport,
} from "../../assets/assetReference";
import { useInView } from "react-intersection-observer";
import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import { ContactEnquiry } from "../../components/Contact/Contact";
import SlideUpAnimation from "../../components/SlideUpAnimation/SlideUpAnimation";
import { Helmet } from "react-helmet-async";
import { useInsights } from "../../context/InsightsContext";
import Insight from "../../data/insight/insightClass";
import GrayBanner from "components/GrayBanner/GrayBanner";
import { useSelector } from "react-redux";
import { getIsMobile } from "features/app/UIRelatedSlice";
import RedLine from "components/Deco/RedLine";
import FAQ from "components/FAQ/FAQ";

export default function HomePage() {
  return (
    <>
      <Helmet>
        <title>WeBridge | Wind Energy Bridge</title>
        <meta
          name="description"
          content="WeBridge is an independent advisory firm bridging European offshore wind developers and international suppliers, providing specialist advisory services for the renewable energy industry."
        />
        <meta
          name="keywords"
          content="WeBridge, wind energy bridge, offshore wind energy, offshore wind developers, offshore wind suppliers"
        />
        <meta property="og:title" content="WeBridge | Wind Energy Bridge" />
        <meta
          property="og:description"
          content="WeBridge is an independent advisory firm bridging European offshore wind developers and international suppliers, providing specialist advisory services for the renewable energy industry."
        />
        <meta name="twitter:title" content="WeBridge | Wind Energy Bridge" />
        <meta
          name="twitter:description"
          content="WeBridge is an independent advisory firm bridging European offshore wind developers and international suppliers, providing specialist advisory services for the renewable energy industry."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "@id": "https://www.windenergybridge.com/#organization",
            name: "WeBridge",
            alternateName: "Wind Energy Bridge",
            url: "https://www.windenergybridge.com",
            logo: "https://www.windenergybridge.com/og-default.jpg",
            description:
              "WeBridge is an independent advisory firm bridging European offshore wind developers and international suppliers, providing specialist advisory services for the renewable energy industry.",
            address: {
              "@type": "PostalAddress",
              streetAddress: "71-75 Shelton Street, Covent Garden",
              addressLocality: "London",
              postalCode: "WC2H 9JQ",
              addressCountry: "GB",
            },
            telephone: "+44 (0)20 8036 4941",
            email: "contact@windenergybridge.com",
            sameAs: ["https://www.linkedin.com/company/webridge-global/"],
            knowsAbout: [
              "Offshore wind energy",
              "Wind farm development",
              "Renewable energy consulting",
              "Supply chain advisory",
              "Offshore wind investment",
              "Offshore wind insurance",
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "@id": "https://www.windenergybridge.com/#website",
            name: "WeBridge",
            alternateName: "Wind Energy Bridge",
            url: "https://www.windenergybridge.com",
            publisher: {
              "@type": "Organization",
              "@id": "https://www.windenergybridge.com/#organization",
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DefinedTermSet",
            name: "Offshore Wind Industry Glossary",
            description:
              "Key terms and concepts in the offshore wind energy industry.",
            hasDefinedTerm: [
              {
                "@type": "DefinedTerm",
                name: "Offshore Wind Energy",
                description:
                  "Electricity generated by wind turbines installed in bodies of water, typically on the continental shelf, using fixed foundations or floating platforms connected to the onshore grid via subsea cables.",
              },
              {
                "@type": "DefinedTerm",
                name: "Contracts for Difference (CfD)",
                description:
                  "The UK government's primary support mechanism for offshore wind. Developers bid for a guaranteed strike price per MWh; the scheme pays the difference between strike and market price, providing long-term revenue certainty.",
              },
              {
                "@type": "DefinedTerm",
                name: "Carbon Border Adjustment Mechanism (CBAM)",
                description:
                  "An EU regulation placing a carbon price on imports of carbon-intensive goods entering the EU, requiring purchasers of CBAM certificates equivalent to the EU carbon price for steel, aluminium, cement and electricity imports.",
              },
              {
                "@type": "DefinedTerm",
                name: "EPCI Contractor",
                description:
                  "An Engineering, Procurement, Construction and Installation contractor responsible for delivering major offshore wind infrastructure packages from design through to commissioning.",
              },
              {
                "@type": "DefinedTerm",
                name: "Offshore Wind Supply Chain",
                description:
                  "The network of manufacturers, fabricators, logistics providers and service companies that supply turbines, foundations, cables, installation vessels and other components needed to build and operate offshore wind farms.",
              },
              {
                "@type": "DefinedTerm",
                name: "Floating Offshore Wind",
                description:
                  "Offshore wind turbines mounted on buoyant floating platforms moored to the seabed, enabling deployment in deeper waters where fixed foundations are not feasible.",
              },
            ],
          })}
        </script>
      </Helmet>
      <div className={"home-page-container mb-[200px] h-full"}>
        <div className="relative w-screen h-screen -mt-[50px]">
          <img
            className="w-full h-full object-cover"
            src={homeBackground}
            alt="Offshore wind turbines at sea — WeBridge homepage"
          />
          {/* <div className="absolute inset-0 bg-white opacity-50"></div> */}
          <div className="absolute left-0 top-0 h-full w-full">
            <div className="h-full w-full px-4 mobile:px-6 sm:px-10 mx-auto flex flex-col items-start justify-center">
              <p className="text-white font-body-condensed text-lg mb-2">
                OFFSHORE WIND ENERGY
              </p>
              <h1 className="text-white text-shadow-lg font-bold text-6xl font-body-condensed leading-[1.2]">
                WORKING TOGETHER
                <br />
                FOR A SUSTAINABLE FUTURE
              </h1>

              <button
                type="button"
                className="border-2 border-white px-10 py-3 pb-3 mt-6 self-start"
              >
                {/* <a
                  href="https://www.gwsce.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-main text-base text-start font-bold font-body-condensed text-white"
                >
                  <p className="font-normal">Monday, 16 June 2025</p>
                  <p className="text-lg">Exclusive London Networking Event</p>
                </a> */}
                <NavLink
                  to="/about-us"
                  className="text-xs font-bold font-body-condensed text-white"
                >
                  LEARN MORE
                </NavLink>
              </button>
            </div>
          </div>
        </div>
        {/* <ImgRedBannerTemplate
          backgroundImg={{
            src: windmillVideosArray[0],
            alt: "a video of many offshore windmill turbine generators, video zooms out",
            type: "video",
          }}
          // backgroundImg={{ src: windmillPhotosArray[2], alt: "offshore windmill photo" }}
          h1={["Working Together", "for a Sustainable Future"]}
          redBannerContent={[
            "We are WeBridge",
            "Connecting European Offshore Wind Projects with International Supply Chains.",
          ]}
          redBannerLink="/about-us"
          OtherContent={<OtherContent />}
          num={3}
          // delay={1.5}
          duration={2}
        /> */}
        <WhoWeAre />
        <div className="content flex flex-col gap-40">
          <WhatWeOffer />
          <WhyWeBridge />
          <Insights />
          <FAQ />
          <ContactUs />
        </div>
      </div>
    </>
  );
}

function WhoWeAre() {
  return (
    <GrayBanner
      tag="WHO WE ARE"
      title={
        <span>
          Wind Energy
          <br />
          Bridge
        </span>
      }
      button={{ color: "main", text: "CONTACT US" }}
      content={
        <>
          <p className="mb-4">
            Connecting European Offshore Wind Projects with International Supply
            Chains.
          </p>
          <p>
            We are WeBridge. WeBridge stands as an independent advisory firm,
            bridging the gap between European offshore wind developers and
            international suppliers and service providers.
          </p>
        </>
      }
      padding={true}
    />
  );
}

function WhatWeOffer() {
  const services = [
    {
      title: "For Developers",
      icon: IconOptimisation,
      highlight:
        "Bridging demand and supply gaps to help European developers build successfully.",
      link: "/services/for-developers",
    },
    {
      title: "For Suppliers",
      icon: IconEntry,
      highlight:
        "Tailored business development for a successful entry into the European market.",
      link: "/services/for-suppliers",
    },
    {
      title: "For Investors",
      icon: IconInsights,
      highlight:
        "Market insight, risk assessment and due diligence to support investment decisions.",
      link: "/services/for-investors",
    },
    {
      title: "For Insurers",
      icon: IconRiskAssessmentSupport,
      highlight:
        "Navigating the offshore wind insurance landscape with confidence.",
      link: "/services/for-insurers",
    },
  ];

  return (
    <div className="block">
      <SlideUpAnimation triggerOnce={false}>
        <div className="services-header">
          <div>
            <p className="tag">WHAT WE OFFER</p>
            <h2 className="subheading">Our Services</h2>
            <RedLine />
          </div>
          <p className="services-intro">
            Specialist advisory across the full offshore wind value chain —
            from developers and suppliers through to investors and insurers.
          </p>
        </div>
        <div className="services-grid mt-20">
          {services.map((service, index) => (
            <NavLink to={service.link} className="service-card" key={service.title}>
              <span className="service-card-index">0{index + 1}</span>
              <service.icon className="service-card-icon" />
              <h3>{service.title}</h3>
              <p>{service.highlight}</p>
              <span className="service-card-link">
                Learn more <IconRight />
              </span>
            </NavLink>
          ))}
        </div>
      </SlideUpAnimation>
    </div>
  );
}

function WhyWeBridge() {
  //   {
  //     title: "Market Supply-Demand Analysis",
  //     icon: IconAnalysis,
  //     content: [
  //         "Provide valuable insights to European developers, enabling them to make informed decisions, identify potential opportunities for collaboration with international suppliers, and maximize the value of their offshore wind projects.",
  //         "Highlight the competitive advantages and unique offerings of international suppliers that can meet the market demand effectively.",
  //         "Suggest strategies for developers to optimise their supply chains, mitigate risks, and ensure timely project delivery.",
  //     ]
  // },
  const companySellingPoint = [
    {
      icon: IconExperience,
      title: "Extensive Experience",
      content: [
        "A strong team with successful track record in European offshore wind market",
      ],
    },
    {
      icon: IconIndustry,
      title: "Deep Industry Knowledge",
      content: [
        "A thorough understanding of European offshore wind market and developer’s requirements",
      ],
    },
    {
      icon: IconRelationship,
      title: "Strong Network",
      content: [
        "Established connections with key industry stakeholders, including developers and EPCI contractors",
      ],
    },
    {
      icon: IconBridge,
      title: "Cultural Bridge",
      content: [
        "Effective communication and collaboration between International and European counterparts",
      ],
    },
  ];
  return (
    <div>
      <p className="tag">HOW ARE WE DIFFERENT</p>
      <h2 className="subheading">Why WeBridge</h2>
      <RedLine />
      <div className="pillars-row mt-20">
        {companySellingPoint.map((i, index) => {
          return (
            <SlideUpAnimation
              triggerOnce={true}
              transition={{ delay: index * 1 }}
              key={i.title}
            >
              <div className="pillar">
                <div className="pillar-node">
                  <i.icon width={28} height={28} />
                </div>
                <h3>{i.title}</h3>
                <p>{i.content[0]}</p>
              </div>
            </SlideUpAnimation>
          );
        })}
      </div>
    </div>
  );
}

function Insights() {
  const [activeTab, setActiveTab] = useState("articles");
  const { articles, weekly, events } = useInsights();
  const tabs = [
    { label: "Articles", key: "articles", items: articles, href: "/insights/articles" },
    { label: "Weekly", key: "weekly", items: weekly, href: "/insights/weekly" },
    { label: "Events", key: "events", items: events, href: "/insights/events" },
  ];
  const activeTabData = tabs.find((t) => t.key === activeTab) || tabs[0];

  const excerptOf = (i: Insight) => {
    if (i.preview) return i.preview;
    const paragraphs = Array.from(
      (i.bodyHtml || "").matchAll(/<p(?:\s+class="([^"]*)")?[^>]*>([\s\S]*?)<\/p>/gi)
    );
    const clean = (html: string) =>
      html.replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
    const first = paragraphs
      .map((m) => ({ cls: m[1], text: clean(m[2]) }))
      .find((p) => p.cls !== "article-date" && p.text.length > 60);
    const text = first?.text || "";
    return text.length > 150 ? `${text.slice(0, 150).trim()}…` : text;
  };

  const renderCards = (items: Insight[]) =>
    items.slice(0, 3).map((i) => (
      <NavLink to={i.canonicalPath} target="_blank" className="insight-card" key={i.id || i.title}>
        <div className={`insight-card-media ${i.collection === "WEEKLY" ? "is-weekly" : ""}`}>
          <img src={i.img.src} alt={i.descriptiveAlt} />
        </div>
        <span className="insight-card-category">{i.category}</span>
        <h3>{i.title}</h3>
        <p>{excerptOf(i)}</p>
        <span className="insight-card-link">
          Read <IconRight />
        </span>
      </NavLink>
    ));

  return (
    <div className="w-full">
      <div className="services-header">
        <div>
          <p className="tag">FROM THE INSIGHTS DESK</p>
          <h2 className="subheading">WeBridge Insights</h2>
          <RedLine />
        </div>
        <div className="insights-tabs">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`insights-tab ${activeTab === t.key ? "active" : ""}`}
              type="button"
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="insights-cards-grid mt-20">{renderCards(activeTabData.items)}</div>
      <NavLink to={activeTabData.href} className="insights-view-all">
        View all {activeTabData.label.toLowerCase()} <IconRight />
      </NavLink>
    </div>
  );
}

function ContactUs() {
  return (
    <div className="block contact-block">
      <h2 className="subheading">Contact Us</h2>
      <ContactEnquiry />
    </div>
  );
}

function SlideFromSlideAnimation(props: any) {
  const [ref, inView] = useInView({
    triggerOnce: props.triggerOnce,
    rootMargin: props.rootMargin || "-10% 0px",
  });

  const isMobile = useSelector(getIsMobile);
  if (isMobile) {
    return <div className="feature">{props.children}</div>;
  }

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: props.initial || 0 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 1, ease: "easeOut" }}
      className="feature"
    >
      {props.children}
    </motion.div>
  );
}
